import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Tab, Tabs } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { differenceInCalendarDays, format, isAfter, startOfDay, subDays } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import type { DateRange } from 'react-day-picker';

import { useAuth } from '@/hooks/useAuth';
import {
  getDashboardOverviewAnalytics,
  getDashboardToday,
  isDashboardPendingResponse,
  type DashboardOverviewAnalyticsResponse,
  type DashboardMealPlanActionItem,
} from '@/lib/api/dashboard';
import { getClientsDashboard } from '@/lib/api/clients';
import { getNutritionistProfile, getAuthSubscription } from '@/lib/api/auth';
import {
  getSubscriptionExpiryWarning,
  readSubscriptionGateState,
  readSubscriptionSnapshot,
} from '@/lib/subscriptionGate';

import { trackDashboardTabSwitched } from '@/lib/analytics/trackers/dashboard';
import { DashboardHomeHeader } from './components/dashboard-home/DashboardHomeHeader';
import { DashboardSubscriptionExpiryBanner } from './components/dashboard-home/DashboardSubscriptionExpiryBanner';
import { DashboardTodayTab } from './components/dashboard-home/DashboardTodayTab';
import { DashboardAnalyticsTab } from './components/dashboard-home/DashboardAnalyticsTab';
import {
  CS_PRIMARY,
  DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS,
} from './components/dashboard-home/dashboardHomeTokens';

type DashboardTab = 'today' | 'analytics';
type DashboardDateRangePreset = 'lastWeek' | 'lastMonth';

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;

const readFirstString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const normalizeCurrencyCode = (value: unknown): string => {
  const code = readFirstString(value);
  return /^[A-Za-z]{3}$/.test(code) ? code.toUpperCase() : '';
};

const unwrapProfilePayload = (payload: unknown): Record<string, unknown> => {
  const root = asRecord(payload);
  if (!root) return {};
  return asRecord(root.data) ?? root;
};

const resolveProfileDisplayName = (payload: unknown): string => {
  const data = unwrapProfilePayload(payload);
  const personalDetails =
    asRecord(data.personalDetails) ??
    asRecord(data.personal) ??
    asRecord(data.profile) ??
    data;

  return readFirstString(
    personalDetails.displayName,
    data.displayName,
    personalDetails.fullName,
    data.fullName,
    [personalDetails.firstName, personalDetails.lastName].filter(Boolean).join(' '),
  );
};

const resolveProfileCurrency = (payload: unknown): string => {
  const data = unwrapProfilePayload(payload);
  return normalizeCurrencyCode(data.currency);
};

const applyProfileCurrencyToAnalytics = (
  data: DashboardOverviewAnalyticsResponse | undefined,
  profileCurrency: string,
): DashboardOverviewAnalyticsResponse | undefined => {
  if (!data || !profileCurrency) return data;

  return {
    ...data,
    cards: {
      ...data.cards,
      monthlyRevenue: {
        ...data.cards.monthlyRevenue,
        currency: profileCurrency,
      },
    },
  };
};

const DashboardHome = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<DashboardTab>('today');
  const [analyticsDateRange, setAnalyticsDateRange] = useState<DateRange | undefined>(undefined);
  const dashboardDate = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const dashboardTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    []
  );
  const dashboardQueryParams = useMemo(
    () => ({ date: dashboardDate, timezone: dashboardTimezone }),
    [dashboardDate, dashboardTimezone]
  );
  const normalizedAnalyticsDateRange = useMemo(() => {
    if (!analyticsDateRange?.from) return undefined;
    return {
      from: analyticsDateRange.from,
      to: analyticsDateRange.to ?? analyticsDateRange.from,
    };
  }, [analyticsDateRange]);
  const analyticsQueryParams = useMemo(
    () => {
      const params = normalizedAnalyticsDateRange
        ? {
            date: format(normalizedAnalyticsDateRange.to, 'yyyy-MM-dd'),
            startDate: format(normalizedAnalyticsDateRange.from, 'yyyy-MM-dd'),
            endDate: format(normalizedAnalyticsDateRange.to, 'yyyy-MM-dd'),
            timezone: dashboardTimezone,
          }
        : dashboardQueryParams;
      return { ...params, invoiceDateBasis: 'paidDate' as const };
    },
    [dashboardQueryParams, dashboardTimezone, normalizedAnalyticsDateRange]
  );
  const analyticsDateRangeLabel = useMemo(() => {
    if (!normalizedAnalyticsDateRange) return 'Last 30 days';
    const { from, to } = normalizedAnalyticsDateRange;
    if (from.getTime() === to.getTime()) {
      return format(from, 'MMM d, yyyy');
    }
    return `${format(from, 'MMM d, yyyy')} - ${format(to, 'MMM d, yyyy')}`;
  }, [normalizedAnalyticsDateRange]);

  const userId = user?.id ?? 'anonymous';

  const todayQuery = useQuery({
    queryKey: ['dashboard', 'today', userId, dashboardQueryParams],
    queryFn: () => getDashboardToday(dashboardQueryParams),
    enabled: !authLoading && Boolean(user),
    refetchInterval: 5 * 60 * 1000,
  });

  const analyticsQuery = useQuery({
    queryKey: ['dashboard', 'overview-analytics', userId, analyticsQueryParams],
    queryFn: () => getDashboardOverviewAnalytics(analyticsQueryParams),
    enabled: !authLoading && Boolean(user),
    refetchInterval: 5 * 60 * 1000,
  });

  const clientsBootstrapQuery = useQuery({
    queryKey: ['dashboard', 'clients-bootstrap-total', userId],
    queryFn: () => getClientsDashboard({ page: 1, limit: 1, status: 'all' }),
    enabled: !authLoading && Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  const profileQuery = useQuery({
    queryKey: ['nutritionist', 'profile', 'dashboard-defaults', userId],
    queryFn: getNutritionistProfile,
    enabled: !authLoading && Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  const subscriptionQuery = useQuery({
    queryKey: ['auth', 'subscription', 'dashboard', userId],
    queryFn: getAuthSubscription,
    enabled: !authLoading && Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  const subscriptionExpiryWarning = useMemo(() => {
    const gateState = readSubscriptionGateState();
    const subscription = subscriptionQuery.data?.subscription
      ?? gateState?.subscription
      ?? (() => {
        const snapshot = readSubscriptionSnapshot();
        if (!snapshot) {
          return null;
        }

        return {
          allowed: snapshot.allowed,
          endsAt: snapshot.endsAt,
          enforcementEnabled: snapshot.enforcementEnabled,
        };
      })();

    return getSubscriptionExpiryWarning(subscription, {
      accessBlocked: gateState?.accessBlocked,
    });
  }, [subscriptionQuery.data]);

  const todayData = todayQuery.data && !isDashboardPendingResponse(todayQuery.data)
    ? todayQuery.data
    : undefined;
  const analyticsData = analyticsQuery.data && !isDashboardPendingResponse(analyticsQuery.data)
    ? analyticsQuery.data
    : undefined;
  const bootstrapClientTotal = clientsBootstrapQuery.data
    ? clientsBootstrapQuery.data.counts?.all ?? clientsBootstrapQuery.data.pagination.total
    : undefined;
  const hasNoBootstrapClients =
    !clientsBootstrapQuery.isLoading
    && typeof bootstrapClientTotal === 'number'
    && bootstrapClientTotal <= 0;
  const hasNoUnfilteredAnalyticsClients =
    !normalizedAnalyticsDateRange
    && !analyticsQuery.isLoading
    && analyticsData
    && analyticsData.cards.totalClients.count <= 0;
  const showGettingStarted = Boolean(
    hasNoBootstrapClients ||
    (typeof bootstrapClientTotal !== 'number' && hasNoUnfilteredAnalyticsClients)
  );
  const showAnalyticsGettingStarted = showGettingStarted && !normalizedAnalyticsDateRange;
  const profileCurrency = useMemo(() => resolveProfileCurrency(profileQuery.data), [profileQuery.data]);
  const analyticsDataWithProfileCurrency = useMemo(
    () => applyProfileCurrencyToAnalytics(analyticsData, profileCurrency),
    [analyticsData, profileCurrency],
  );

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const profileDisplayName = resolveProfileDisplayName(profileQuery.data);
  const fallbackName =
    readFirstString(
      (user as unknown as Record<string, unknown> | null)?.displayName,
      (user as unknown as Record<string, unknown> | null)?.firstName,
      user?.fullName,
    ) || 'User';
  const greetingLine = `${getGreeting()}, ${profileDisplayName || fallbackName} 👋`;
  const dateLine = format(new Date(), 'EEEE, d MMMM yyyy');

  const handleNavigateToAppointments = () => {
    navigate('/appointments');
  };

  const handleNavigateToAppointmentsCalendar = () => {
    navigate('/appointments/calendar');
  };

  const handleNavigateToAddFirstClient = () => {
    navigate('/clients/add');
  };

  const handleNavigateToAvailability = () => {
    navigate('/profile?tab=availability');
  };

  const handleNavigateToBillingSettings = () => {
    navigate('/profile?view=invoice-tax-details');
  };

  const handleAnalyticsPresetSelect = (preset: DashboardDateRangePreset) => {
    const today = new Date();
    setAnalyticsDateRange({
      from: subDays(today, preset === 'lastWeek' ? 6 : 29),
      to: today,
    });
  };

  const handleAnalyticsDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from && range.to) {
      const today = startOfDay(new Date());
      if (isAfter(startOfDay(range.from), today) || isAfter(startOfDay(range.to), today)) return;
      const dayCount = Math.abs(differenceInCalendarDays(range.to, range.from)) + 1;
      if (dayCount > DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS) return;
    }
    setAnalyticsDateRange(range);
  };

  const handleReassignMealPlanFromDashboard = (row: DashboardMealPlanActionItem) => {
    const clientId = row.clientId?.trim();
    if (!clientId) return;
    navigate(`/clients/${encodeURIComponent(clientId)}/details-v2/meal-plan?addMeal=1`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box data-demo-id="dashboard-home" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            overflow: 'hidden',
            borderRadius: '28px',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            bgcolor: '#fff',
          }}
        >
          <Box sx={{ bgcolor: '#fbfbfb', p: { xs: 2, sm: 2.5, lg: 3 } }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: '12px',
                border: 'none',
                overflow: 'hidden',
              }}
            >
              <DashboardHomeHeader
                authLoading={authLoading}
                greetingLine={greetingLine}
                dateLine={dateLine}
                analyticsDateRange={analyticsDateRange}
                analyticsDateRangeLabel={analyticsDateRangeLabel}
                showAnalyticsDateRangeControl={activeTab === 'analytics'}
                onAnalyticsDateRangeChange={handleAnalyticsDateRangeChange}
                onAnalyticsPresetSelect={handleAnalyticsPresetSelect}
                onClearAnalyticsDateRange={() => setAnalyticsDateRange(undefined)}
              />
              <Tabs
                data-demo-id="dashboard-tabs"
                value={activeTab}
                onChange={(_, v: DashboardTab) => {
                  setActiveTab(v);
                  trackDashboardTabSwitched({
                    tab: v,
                    tab_label: v === 'today' ? 'Today' : 'Practice Insights',
                  });
                }}
                sx={{
                  minHeight: 46,
                  px: { xs: 2, sm: 3 },
                  borderBottom: '1px solid #e5e7eb',
                  '& .MuiTabs-indicator': { bgcolor: CS_PRIMARY, height: 2 },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 400,
                    color: '#000',
                    minHeight: 46,
                    '&.Mui-selected': { color: CS_PRIMARY, fontWeight: 600 },
                  },
                }}
              >
                <Tab data-demo-id="dashboard-today-tab" label="Today" value="today" />
                <Tab data-demo-id="dashboard-practice-insights-tab" label="Practice Insights" value="analytics" />
              </Tabs>
            </Paper>

            {subscriptionExpiryWarning ? (
              <Box sx={{ mt: 2 }}>
                <DashboardSubscriptionExpiryBanner warning={subscriptionExpiryWarning} />
              </Box>
            ) : null}

            <Box sx={{ mt: 2 }}>
              {activeTab === 'today' && (
                <Box data-demo-id="dashboard-today-panel">
                <DashboardTodayTab
                  data={todayData}
                  isLoading={todayQuery.isLoading || (todayQuery.isFetching && !todayQuery.data)}
                  isPending={isDashboardPendingResponse(todayQuery.data)}
                  error={todayQuery.isError ? 'Add clients and create meal plans to start seeing dashboard stats and analytics.' : undefined}
                  showGettingStarted={showGettingStarted}
                  onAddFirstClient={handleNavigateToAddFirstClient}
                  onSetAvailability={handleNavigateToAvailability}
                  onSetupBilling={handleNavigateToBillingSettings}
                  onViewCalendar={handleNavigateToAppointments}
                  onViewAllAppointments={handleNavigateToAppointmentsCalendar}
                  onViewAllMealPlans={() => navigate('/meal-planning')}
                  onReassignMealPlan={handleReassignMealPlanFromDashboard}
                  onViewAllInvoices={() => navigate('/invoicing?tab=due')}
                  onInvoicesChanged={() => void todayQuery.refetch()}
                />
                </Box>
              )}
              {activeTab === 'analytics' && (
                <Box data-demo-id="dashboard-practice-insights-panel">
                <DashboardAnalyticsTab
                  data={analyticsDataWithProfileCurrency}
                  isLoading={analyticsQuery.isLoading || (analyticsQuery.isFetching && !analyticsQuery.data)}
                  isPending={isDashboardPendingResponse(analyticsQuery.data)}
                  error={analyticsQuery.isError ? 'Add clients and create meal plans to start seeing dashboard stats and analytics.' : undefined}
                  showGettingStarted={showAnalyticsGettingStarted}
                  onAddFirstClient={handleNavigateToAddFirstClient}
                  onSetAvailability={handleNavigateToAvailability}
                  onSetupBilling={handleNavigateToBillingSettings}
                  dateRangeLabel={analyticsDateRangeLabel}
                  isCustomDateRange={Boolean(normalizedAnalyticsDateRange)}
                />
                </Box>
              )}
            </Box>
          </Box>

        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardHome;
